import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import * as angular from 'angular';
import { downgradeComponent } from '@angular/upgrade/static';

export interface SelectOption {
  text?: string;
  name?: string;
  value: any;
  dead?: boolean;
  translate?: boolean;
  translateArgs?: any;
  tooltip?: string;
  translateTooltip?: boolean;
  group_name?: string;
  fields?: any[];
}

interface GroupedSelectOption {
  [key: string]: SelectOption[];
}

@Component({
  selector: 'xp-select',
  template: `
    <mat-select
      *ngIf="isMultiple"
      [(value)]="value"
      (valueChange)="onValueChange($event)"
      (blur)="onBlurEvent()"
      [id]="id"
      [matTooltip]="tooltip"
      [disabled]="disabled"
      matTooltipPosition="above"
      [placeholder]="placeholder"
      [ngClass]="{ dead: isDeadSelected }"
      [panelClass]="panelClass"
      [matTooltipClass]="state + ' ' + 'above'"
      [ngModel]="value"
      disableOptionCentering
      multiple
    >
      <mat-option *ngIf="isSearchEnabled">
        <ngx-mat-select-search
          [clearSearchInput]="false"
          [ngModel]="searchValue"
          (ngModelChange)="filterOptions($event)"
          [placeholderLabel]="placeholder"
          [noEntriesFoundLabel]="emptyPlaceholder"
        ></ngx-mat-select-search>
      </mat-option>
      <div *ngIf="!isGrouped">
        <mat-option
          [ngClass]="{ dead: option.dead }"
          *ngFor="let option of selectedOptions"
          [value]="option[valueKey]"
          [matTooltip]="option.translateTooltip ? (option.tooltip | translate) : option.tooltip"
          matTooltipPosition="after"
          matTooltipClass="after"
        >
          <span *ngIf="option.translate">{{ option.text || option.name | translate }}</span>
          <span *ngIf="!option.translate">{{ option.text || option.name }}</span>
        </mat-option>
      </div>
      <div *ngIf="isGrouped">
        <mat-optgroup *ngFor="let key of objectKeys(selectedOptions)" [label]="key">
          <mat-option
            [ngClass]="{ dead: option.dead }"
            *ngFor="let option of selectedOptions[key]"
            [value]="option[valueKey]"
            [matTooltip]="option.translateTooltip ? (option.tooltip | translate) : option.tooltip"
            matTooltipPosition="after"
            matTooltipClass="after"
          >
            <span *ngIf="option.translate">{{
              option.text || option.name | translate: option.translateArgs || {}
            }}</span>
            <span *ngIf="!option.translate">{{ option.text || option.name }}</span>
          </mat-option>
        </mat-optgroup>
      </div>
    </mat-select>
    <mat-select
      *ngIf="!isMultiple"
      [(value)]="value"
      (valueChange)="onValueChange($event)"
      (blur)="onBlurEvent()"
      [id]="id"
      [matTooltip]="tooltip"
      [disabled]="disabled"
      [placeholder]="placeholder"
      [ngClass]="{ dead: isDeadSelected }"
      [panelClass]="panelClass"
      disableOptionCentering
      matTooltipPosition="above"
      [matTooltipClass]="state + ' ' + 'above'"
    >
      <mat-option *ngIf="isSearchEnabled">
        <ngx-mat-select-search
          [clearSearchInput]="false"
          [ngModel]="searchValue"
          (ngModelChange)="filterOptions($event)"
          [placeholderLabel]="placeholder"
          [noEntriesFoundLabel]="emptyPlaceholder"
        ></ngx-mat-select-search>
      </mat-option>
      <div *ngIf="!isGrouped">
        <mat-option
          [ngClass]="{ dead: option.dead }"
          *ngFor="let option of selectedOptions"
          [value]="option[valueKey]"
          [matTooltip]="option.translateTooltip ? (option.tooltip | translate) : option.tooltip"
          matTooltipPosition="after"
          matTooltipClass="after"
        >
          <span *ngIf="option.translate">{{ option.text || option.name | translate }}</span>
          <span *ngIf="!option.translate">{{ option.text || option.name }}</span>
        </mat-option>
      </div>
      <div *ngIf="isGrouped">
        <mat-optgroup *ngFor="let key of objectKeys(selectedOptions)" [label]="key">
          <mat-option
            [ngClass]="{ dead: option.dead }"
            *ngFor="let option of selectedOptions[key]"
            [value]="option[valueKey]"
            [matTooltip]="option.translateTooltip ? (option.tooltip | translate) : option.tooltip"
            matTooltipPosition="after"
            matTooltipClass="after"
          >
            <span *ngIf="option.translate">{{
              option.text || option.name | translate: option.translateArgs || {}
            }}</span>
            <span *ngIf="!option.translate">{{ option.text || option.name }}</span>
          </mat-option>
        </mat-optgroup>
      </div>
    </mat-select>
    <input #inputRef class="hidden" [id]="id + '1'" [name]="name" type="text" />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class XpSelectComponent implements OnInit, OnChanges {
  @Input() value: any;
  @Output() valueChange = new EventEmitter<any>();
  @Input() options: SelectOption[] = [];
  @Input() isMultiple = false;
  @Input() id = String(Date.now());
  @Input() name = '';
  @Input() disabled = false;
  @Input() tooltip = '';
  @Input() placeholder = '';
  @Input() isSearchEnabled = false;
  @Input() preventEmpty = false;
  @Input() isGrouped = false;
  @Input() panelClass = '';
  @Input() state = '';
  @Input() valueKey = 'value';
  @Input() emptyPlaceholder = 'No options found.';
  @Output() onBlur = new EventEmitter<any>();
  @ViewChild('inputRef') inputRef: ElementRef<HTMLInputElement>;

  selectedOptions: SelectOption[] | GroupedSelectOption = [];
  objectKeys = Object.keys;
  searchValue = '';

  ngOnInit() {
    this.setOptions(this.options);

    if (this.value) {
      setTimeout(() => {
        this.inputRef.nativeElement.value = this.value;
        this.inputRef.nativeElement.dispatchEvent(
          new Event('input', {
            bubbles: true,
            cancelable: true,
          }),
        );
      });
    }

    if (this.preventEmpty && this.options && this.options[0]) {
      this.setFirstValue();
    }
  }

  setOptions(options = []) {
    this.selectedOptions = this.isGrouped
      ? options.reduce(
          (acc, { group_name, ...rest }) => ({ ...acc, [group_name]: [...(acc[group_name] || []), { ...rest }] }),
          {},
        )
      : options;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.options && changes.options.currentValue) {
      this.setOptions(changes.options.currentValue);
      if (this.preventEmpty && changes.options.currentValue[0] && !this.value) {
        this.setFirstValue();
      }
    }

    if (this.preventEmpty && this.options[0] && !this.value) {
      this.setFirstValue();
    }
  }

  setFirstValue(): void {
    if (!this.value) {
      this.value = this.options[0][this.valueKey];
      this.onValueChange(this.value);
    }
  }

  onValueChange(value) {
    this.valueChange.emit(value);
    setTimeout(() => {
      this.inputRef.nativeElement.value = value;
      this.inputRef.nativeElement.dispatchEvent(
        new Event('input', {
          bubbles: true,
          cancelable: true,
        }),
      );
    });
  }

  onBlurEvent() {
    this.inputRef.nativeElement.dispatchEvent(
      new Event('blur', {
        bubbles: true,
        cancelable: true,
      }),
    );
    this.onBlur.emit();
  }

  filterOptions(value) {
    this.setOptions(this.search(value));
  }

  search(value: string): SelectOption[] {
    const filter = value.toLowerCase();
    return this.options.filter((option) => (option.text || option.name).toLowerCase().includes(filter));
  }

  get isDeadSelected(): boolean {
    if ((this.options || []).find) {
      return ((this.options || []).find((option) => option[this.valueKey] === this.value) || {}).dead;
    }
    return false;
  }
}

((window as any).xplenty as angular.IModule).directive(
  'xpSelect',
  downgradeComponent({
    component: XpSelectComponent,
  }),
);
